import type { TSignUpSteps } from '#shared/types/user.types';
import { ESignUpSteps } from '#shared/types/user.types';
import { useAppToast } from '~/composables/shared/useAppToast';

export interface ICredentialsData {
  username: string;
  email: string;
  password: string;
  viaProvider?: boolean;
  isAgreeWithTnc: boolean;
  isAdult: boolean;
  googleToken: string;
  affiliateCode?: string;
}

const initialStep = ESignUpSteps.Credentials;
const initialCredentials: ICredentialsData = {
  username: '',
  email: '',
  password: '',
  isAgreeWithTnc: false,
  isAdult: false,
  googleToken: '',
  affiliateCode: '',
};

export const useSignUpStore = defineStore('signUp', () => {
  const activeStep = ref<TSignUpSteps>(initialStep);
  const credentials = ref<ICredentialsData>(structuredClone(initialCredentials));
  const { showErrorToast } = useAppToast();
  const signUpInProcess = ref(false);

  async function createUser() {
    const createUserData: ICreateUserData = {
      email: credentials.value.email,
      checkAds: true,
      agreeTerms: credentials.value.isAgreeWithTnc,
      check18: credentials.value.isAdult,
      googleToken: credentials.value.googleToken,
    };

    if (stores.user.socialSignUpIsPending) {
      createUserData.viaProvider = true;
    } else if (credentials.value.password) {
      createUserData.password = credentials.value.password;
      createUserData.displayName = credentials.value.username || credentials.value.email.split('@')[0];
    }

    if (credentials.value.affiliateCode) {
      createUserData.affiliateCode = sanitizeString(credentials.value.affiliateCode);
    }

    const result = await stores.user.createUser(createUserData);

    if (result) {
      return true;
    } else {
      showErrorToast(`Registration failed`);
      return false;
    }
  }

  async function handleSignOut() {
    signUpInProcess.value = false;
    await stores.user.signOutOfFirebase();
    await stores.user.logUserOut();
    navigateTo({ name: ERouteName.LOGGED_OUT });
  }

  function reset() {
    credentials.value = structuredClone(initialCredentials);
    activeStep.value = ESignUpSteps.Credentials;
  }

  function setSignUpStep(step: TSignUpSteps) {
    activeStep.value = step;
  }

  function setSignUpInProcess(value: boolean) {
    signUpInProcess.value = value;
  }

  return {
    activeStep,
    credentials,
    createUser,
    handleSignOut,
    reset,
    signUpInProcess,
    setSignUpStep,
    setSignUpInProcess,
  };
});
